import { useCallback, useEffect, useRef } from 'react';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export const useRecaptcha = () => {
  const isReady = useRef(false);

  useEffect(() => {
    // Only check if reCAPTCHA script is already loaded (by search page)
    // Don't load the script here - it should only be loaded on the search page
    if (typeof window !== 'undefined' && window.grecaptcha) {
      window.grecaptcha.ready(() => {
        isReady.current = true;
      });
    }
  }, []);

  const executeRecaptcha = useCallback(async (action: string = 'search'): Promise<string | null> => {
    const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
    
    if (!siteKey) {
      console.warn('reCAPTCHA site key not found');
      return null;
    }

    // Check if grecaptcha is available (script must be loaded by the search page)
    if (!window.grecaptcha) {
      console.warn('reCAPTCHA script not loaded. It should only be loaded on the search page.');
      return null;
    }

    if (!isReady.current && window.grecaptcha) {
      // Wait for grecaptcha to be ready
      return new Promise((resolve) => {
        window.grecaptcha.ready(async () => {
          isReady.current = true;
          try {
            const token = await window.grecaptcha.execute(siteKey, { action });
            resolve(token);
          } catch (error) {
            console.error('reCAPTCHA execution error:', error);
            resolve(null);
          }
        });
      });
    }

    if (isReady.current && window.grecaptcha) {
      try {
        const token = await window.grecaptcha.execute(siteKey, { action });
        return token;
      } catch (error) {
        console.error('reCAPTCHA execution error:', error);
        return null;
      }
    }

    return null;
  }, []);

  return { executeRecaptcha, isReady: isReady.current };
};

